import { apiClient } from './api';
import { getBrandConfig } from '@/brands';

export interface AnalyticsTimeRange {
  label: string;
  value: string;
  days: number;
}

export interface DateRangeFilter {
  startDate: string;
  endDate: string;
}

export interface AnalyticsFilters {
  timeRange?: string;
  dateRange?: DateRangeFilter;
}

// Chart Data Interfaces
export interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
  category?: string;
}

export interface TrendData {
  current: number;
  previous: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
}

export interface CategoryData {
  name: string;
  value: number;
  color: string;
  percentage: number;
}

export interface SlaStatus {
  metricKey: string;
  displayName: string;
  currentValue: number;
  thresholdGood: number;
  thresholdWarning: number;
  unit: string;
  status: 'good' | 'warning' | 'critical';
  isLowerBetter: boolean;
}

export interface Insight {
  id: string;
  type: 'anomaly' | 'trend' | 'record' | 'correlation';
  severity: 'info' | 'warning' | 'critical';
  icon: string;
  title: string;
  message: string;
  metric: string;
  currentValue: number;
  baselineValue: number;
  deviationPercent: number;
  source: string;
  timestamp: string;
}

// Incident Lifecycle / Funnel Interfaces
export interface LifecycleStage {
  name: string;
  count: number;
  avgDurationHours: number | null;
}

export interface DropOffRate {
  from: string;
  to: string;
  rate: number;
}

export interface IncidentLifecycleData {
  stages: LifecycleStage[];
  dropOffRates: DropOffRate[];
  bottleneck: { stage: string; avgDurationHours: number } | null;
}

// Risk Posture Interfaces
export interface RiskFactor {
  name: string;
  value: number;
  impact: 'positive' | 'negative' | 'neutral';
}

export interface TopRisk {
  title: string;
  source: string;
  severity: string;
  score: number;
  description: string;
}

export interface DomainScore {
  score: number;
  weight: number;
  factors: RiskFactor[];
}

export interface RiskPosture {
  compositeScore: number;
  previousScore?: number;
  scoreBreakdown: {
    incidents: DomainScore;
    threats: DomainScore;
    investigations: DomainScore;
    executiveProtection: DomainScore;
  };
  riskByDomain: CategoryData[];
  topRisks: TopRisk[];
  kpis: {
    mttr: TrendData;
    mttd: TrendData;
    mttrBenchmark: number;
    mttdBenchmark: number;
  };
  trend: ChartDataPoint[];
}

// Geographic Analytics Interfaces
export interface GeoPoint {
  id: string;
  name?: string;
  latitude: number;
  longitude: number;
}

export interface GeoActivity extends GeoPoint {
  riskLevel: string;
  type: string;
  startDatetime?: string;
}

export interface GeoVenue extends GeoPoint {
  address?: string;
  venueType?: string;
}

export interface GeoFence {
  id: string;
  name: string;
  centerLatitude: number;
  centerLongitude: number;
  radiusMeters: number;
  fenceType: string;
}

export interface IncidentLocation {
  location: string;
  count: number;
}

export interface LocationAliasWarning {
  canonical: string;
  variants: string[];
}

export interface GeographicDataQuality {
  totalIncidents: number;
  incidentsWithLocation: number;
  incidentsWithoutLocation: number;
  locationAliasWarnings: LocationAliasWarning[];
  epActivitiesTotal: number;
  epVenuesTotal: number;
  epGeofencesTotal: number;
}

export interface GeographicAnalytics {
  executiveProtection: {
    activities: GeoActivity[];
    venues: GeoVenue[];
    geofences: GeoFence[];
  };
  incidentLocations: IncidentLocation[];
  dataQuality?: GeographicDataQuality;
}

// Risk Scoring and Correlation Interfaces
export interface RiskScoringData {
  averageRiskScore: number;
  averageConfidence: number;
  averageLikelihood: number;
  averageImpact: number;
  totalAssessed: number;
  byRiskLevel: Record<string, number>;
}

export interface ThreatCorrelationData {
  totalCorrelations: number;
  verifiedCorrelations: number;
  predictionAccuracy: number;
  averageCorrelationScore: number;
}

export interface InvestigationCorrelationData {
  totalCorrelations: number;
  byType: Record<string, number>;
  averageConfidence: number;
}

export interface OpenedVsClosedDataPoint {
  date: string;
  opened: number;
  closed: number;
  netBacklog: number;
}

// Analytics Data Interfaces
export interface IncidentAnalytics {
  totalIncidents: TrendData;
  criticalIncidents: TrendData;
  averageResolutionTime: TrendData;
  averageDetectionTime: TrendData;
  averageClosureTime: TrendData;
  incidentsByStatus: CategoryData[];
  incidentsBySeverity: CategoryData[];
  incidentsByCategory: CategoryData[];
  incidentsTrend: ChartDataPoint[];
  resolutionTimeTrend: ChartDataPoint[];
  detectionTimeTrend: ChartDataPoint[];
  closureTimeTrend: ChartDataPoint[];
  monthlyComparison: ChartDataPoint[];
  slaStatuses?: SlaStatus[];
  lifecycle?: IncidentLifecycleData;
}

export interface InvestigationAnalytics {
  totalInvestigations: TrendData;
  activeInvestigations: TrendData;
  averageInvestigationTime: TrendData;
  overdueCases: TrendData;
  investigationsByStatus: CategoryData[];
  investigationsByPriority: CategoryData[];
  investigationsByCategory: CategoryData[];
  investigationsTrend: ChartDataPoint[];
  caseClosureRate: ChartDataPoint[];
  openedVsClosed: OpenedVsClosedDataPoint[];
  correlations: InvestigationCorrelationData;
  slaStatuses?: SlaStatus[];
}

export interface ThreatAnalytics {
  totalThreats: TrendData;
  activeThreats: TrendData;
  threatLevel: TrendData;
  threatsBySeverity: CategoryData[];
  threatsByType: CategoryData[];
  threatsTrend: ChartDataPoint[];
  recentTrends: {
    last24h: number;
    last7d: number;
    last30d: number;
  };
  riskScoring: RiskScoringData;
  correlations: ThreatCorrelationData;
}

export interface ExecutiveProtectionAnalytics {
  totalActivities: TrendData;
  protectedPersons: TrendData;
  securityEvents: TrendData;
  activitiesByType: CategoryData[];
  eventsByRiskLevel: CategoryData[];
  protectionCoverage: CategoryData[];
  activityTrend: ChartDataPoint[];
  riskAssessments: ChartDataPoint[];
  responseTime: TrendData;
}

export interface DataAvailability {
  status: 'live' | 'cached' | 'unavailable';
  historyAvailable: boolean;
  oldestSnapshot?: string;
}

export interface OverallAnalytics {
  summary: {
    totalIncidents: number;
    totalInvestigations: number;
    totalThreats: number;
    totalActivities: number;
  };
  monthlyOverview: ChartDataPoint[];
}

export interface AnalyticsData {
  incidents: IncidentAnalytics;
  investigations: InvestigationAnalytics;
  threats: ThreatAnalytics;
  executiveProtection: ExecutiveProtectionAnalytics;
  overall: OverallAnalytics;
  riskPosture?: RiskPosture;
  dataAvailability?: Record<string, DataAvailability>;
  lastUpdated: string;
  insights?: Insight[];
}

export interface CustomReportConfig {
  title: string;
  description?: string;
  modules: string[];
  metrics: string[];
  chartTypes: string[];
  filters: AnalyticsFilters;
  schedule?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
  };
}

export interface ExportOptions {
  format: 'pdf' | 'excel' | 'csv' | 'json';
  includeCharts: boolean;
  includeRawData: boolean;
  timeRange: string;
  filters: AnalyticsFilters;
}

class AnalyticsService {
  // Time Range Options
  getTimeRangeOptions(): AnalyticsTimeRange[] {
    return [
      { label: 'Last 7 Days', value: '7d', days: 7 },
      { label: 'Last 30 Days', value: '30d', days: 30 },
      { label: 'Last 90 Days', value: '90d', days: 90 },
      { label: 'Last 6 Months', value: '6m', days: 180 },
      { label: 'Last Year', value: '1y', days: 365 },
      { label: 'Custom Range', value: 'custom', days: 0 }
    ];
  }

  /**
   * Get comprehensive analytics data
   */
  async getAnalyticsData(filters: AnalyticsFilters = {}): Promise<AnalyticsData> {
    const queryParams = new URLSearchParams();

    if (filters.timeRange) {queryParams.append('timeRange', filters.timeRange);}
    if (filters.dateRange) {
      queryParams.append('startDate', filters.dateRange.startDate);
      queryParams.append('endDate', filters.dateRange.endDate);
    }
    const url = `/api/v1/analytics${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<AnalyticsData>(url);

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch analytics data');
  }

  /**
   * Get incident analytics specifically
   */
  async getIncidentAnalytics(filters: AnalyticsFilters = {}): Promise<IncidentAnalytics> {
    const queryParams = new URLSearchParams();

    if (filters.timeRange) {queryParams.append('timeRange', filters.timeRange);}
    if (filters.dateRange) {
      queryParams.append('startDate', filters.dateRange.startDate);
      queryParams.append('endDate', filters.dateRange.endDate);
    }

    const url = `/api/v1/analytics/incidents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<IncidentAnalytics>(url);

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch incident analytics');
  }

  /**
   * Get investigation analytics specifically
   */
  async getInvestigationAnalytics(filters: AnalyticsFilters = {}): Promise<InvestigationAnalytics> {
    const queryParams = new URLSearchParams();

    if (filters.timeRange) {queryParams.append('timeRange', filters.timeRange);}
    if (filters.dateRange) {
      queryParams.append('startDate', filters.dateRange.startDate);
      queryParams.append('endDate', filters.dateRange.endDate);
    }

    const url = `/api/v1/analytics/investigations${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<InvestigationAnalytics>(url);

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch investigation analytics');
  }

  /**
   * Get threat analytics specifically
   */
  async getThreatAnalytics(filters: AnalyticsFilters = {}): Promise<ThreatAnalytics> {
    const queryParams = new URLSearchParams();

    if (filters.timeRange) {queryParams.append('timeRange', filters.timeRange);}
    if (filters.dateRange) {
      queryParams.append('startDate', filters.dateRange.startDate);
      queryParams.append('endDate', filters.dateRange.endDate);
    }

    const url = `/api/v1/analytics/threats${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<ThreatAnalytics>(url);

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch threat analytics');
  }

  /**
   * Get executive protection analytics specifically
   */
  async getExecutiveProtectionAnalytics(filters: AnalyticsFilters = {}): Promise<ExecutiveProtectionAnalytics> {
    const queryParams = new URLSearchParams();

    if (filters.timeRange) {queryParams.append('timeRange', filters.timeRange);}
    if (filters.dateRange) {
      queryParams.append('startDate', filters.dateRange.startDate);
      queryParams.append('endDate', filters.dateRange.endDate);
    }

    const url = `/api/v1/analytics/executive-protection${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<ExecutiveProtectionAnalytics>(url);

    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch executive protection analytics');
  }

  /**
   * Get risk posture data
   */
  async getRiskPosture(filters: AnalyticsFilters = {}): Promise<RiskPosture> {
    const queryParams = new URLSearchParams();
    if (filters.timeRange) {queryParams.append('timeRange', filters.timeRange);}
    if (filters.dateRange) {
      queryParams.append('startDate', filters.dateRange.startDate);
      queryParams.append('endDate', filters.dateRange.endDate);
    }
    const url = `/api/v1/analytics/risk-posture${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<RiskPosture>(url);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch risk posture');
  }

  /**
   * Get geographic analytics data
   */
  async getGeographicAnalytics(): Promise<GeographicAnalytics> {
    const response = await apiClient.get<GeographicAnalytics>('/api/v1/analytics/geographic');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch geographic analytics');
  }

  /**
   * Create custom report
   */
  async createCustomReport(config: CustomReportConfig): Promise<{ reportId: string; downloadUrl: string }> {
    const response = await apiClient.post<{ reportId: string; downloadUrl: string }>('/api/v1/analytics/reports', config);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to create custom report');
  }

  /**
   * Export analytics data as a binary file (CSV, Excel, or JSON)
   */
  async exportAnalytics(options: ExportOptions): Promise<Blob> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    const token = localStorage.getItem('token');

    const response = await fetch(`${baseUrl}/api/v1/analytics/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw new Error('Failed to export analytics');
    }

    return response.blob();
  }

  /**
   * Utility function to calculate trend direction
   */
  calculateTrend(current: number, previous: number): 'up' | 'down' | 'stable' {
    const changePercent = previous > 0 ? ((current - previous) / previous) * 100 : 0;
    if (Math.abs(changePercent) < 1) {return 'stable';}
    return changePercent > 0 ? 'up' : 'down';
  }

  /**
   * Utility function to format percentage
   */
  formatPercentage(value: number): string {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  }

  /**
   * Utility function to format numbers
   */
  formatNumber(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  }

  /**
   * Utility function to get color for trend
   */
  getTrendColor(trend: 'up' | 'down' | 'stable', isPositive: boolean = true): string {
    if (trend === 'stable') {return 'text-secondary-600 dark:text-secondary-400';}

    if (isPositive) {
      return trend === 'up'
        ? 'text-success-600 dark:text-success-400'
        : 'text-danger-600 dark:text-danger-400';
    } else {
      return trend === 'up'
        ? 'text-danger-600 dark:text-danger-400'
        : 'text-success-600 dark:text-success-400';
    }
  }

  /**
   * Utility function to get brand-specific chart colors for categories
   */
  getChartColors(): string[] {
    const brand = getBrandConfig(import.meta.env.VITE_BRAND);
    return brand.chartColors;
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;